<template>
  <div class="space-y-6">
    <!-- 헤더 섹션 -->
    <div class="glass-card p-6">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 class="text-2xl font-bold text-gradient mb-2">
            로또 분석 대시보드
          </h2>
          <p class="text-gray-600">
            전체 회차별 당첨번호 히스토리 및 실시간 패턴 분석
          </p>
        </div>
        <div class="mt-4 md:mt-0">
          <div class="flex items-center space-x-4">
            <div class="text-center">
              <div class="text-2xl font-bold text-green-600">
                {{ lastUpdate }}
              </div>
              <div class="text-sm text-gray-500">최신 회차</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 최신 당첨번호 -->
    <div class="glass-card p-8">
      <div v-if="latestNumbers" class="space-y-6">
        <div class="flex items-center justify-between">
          <h3 class="text-2xl font-bold text-gray-800">{{ latestNumbers.round }}회차</h3>
          <span class="text-sm text-gray-500">{{ formatDate(latestNumbers.date) }}</span>
        </div>
        <div class="flex items-center justify-center space-x-4">
          <span
            v-for="(number, index) in latestNumbers.numbers"
            :key="index"
            class="flex items-center justify-center w-14 h-14 rounded-full font-bold text-white text-xl shadow-lg transform hover:scale-110 transition-transform"
            :class="getBallClass(number)"
          >
            {{ number }}
          </span>
          <div class="mx-2 text-gray-400 text-2xl">+</div>
          <span class="flex items-center justify-center w-14 h-14 rounded-full font-bold text-white text-xl shadow-lg bg-gradient-to-r from-orange-400 to-orange-600 border-2 border-yellow-400">
            {{ latestNumbers.bonus }}
          </span>
        </div>
      </div>
    </div>

    <!-- 주요 통계 카드들 -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="stat-card">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100">
            <svg
              class="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">가장 많이 나온 번호</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ mostFrequentNumber.number }}
            </p>
            <p class="text-xs text-gray-500">
              {{ mostFrequentNumber.count }}회 출현
            </p>
          </div>
        </div>
      </div>

      <div class="stat-card">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100">
            <svg
              class="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">홀수 비율</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ oddPercentage }}%
            </p>
            <p class="text-xs text-gray-500">전체 당첨번호 중</p>
          </div>
        </div>
      </div>

      <div class="stat-card">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-purple-100">
            <svg
              class="w-6 h-6 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">연속번호 출현율</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ consecutivePercentage }}%
            </p>
            <p class="text-xs text-gray-500">최근 100회 기준</p>
          </div>
        </div>
      </div>

      <div class="stat-card">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-orange-100">
            <svg
              class="w-6 h-6 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">평균 번호합</p>
            <p class="text-2xl font-semibold text-gray-900">{{ averageSum }}</p>
            <p class="text-xs text-gray-500">6개 번호 평균</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 광고 영역 1 -->
    <AdSenseUnit ad-slot="1889042250" />

    <!-- 패턴 분석 섹션 -->
    <div class="glass-card p-6">
      <h3 class="text-xl font-semibold mb-6 flex items-center">
        <div
          class="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mr-3"
        ></div>
        상세 패턴 분석
      </h3>

      <!-- 패턴 분석 카드들 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- 연속번호 패턴 -->
        <div class="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
          <h4 class="text-lg font-semibold mb-4 flex items-center">
            <div class="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            연속번호 패턴 분석
          </h4>
          <div class="space-y-4">
            <div
              class="flex justify-between items-center p-3 bg-white/60 rounded-lg"
            >
              <span class="text-sm font-medium">2개 연속</span>
              <span class="text-lg font-bold text-blue-600"
                >{{ consecutivePatterns.two }}%</span
              >
            </div>
            <div
              class="flex justify-between items-center p-3 bg-white/60 rounded-lg"
            >
              <span class="text-sm font-medium">3개 연속</span>
              <span class="text-lg font-bold text-green-600"
                >{{ consecutivePatterns.three }}%</span
              >
            </div>
            <div
              class="flex justify-between items-center p-3 bg-white/60 rounded-lg"
            >
              <span class="text-sm font-medium">4개 이상 연속</span>
              <span class="text-lg font-bold text-purple-600"
                >{{ consecutivePatterns.four }}%</span
              >
            </div>
          </div>
        </div>

        <!-- 구간별 분포 -->
        <div
          class="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg"
        >
          <h4 class="text-lg font-semibold mb-4 flex items-center">
            <div class="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
            구간별 번호 분포
          </h4>
          <div class="space-y-3">
            <div
              v-for="(section, index) in sectionDistribution"
              :key="index"
              class="space-y-2"
            >
              <div class="flex justify-between items-center">
                <span class="text-sm font-medium">{{ section.range }}</span>
                <span class="text-sm font-bold">{{ section.percentage }}%</span>
              </div>
              <div class="w-full bg-white/60 rounded-full h-2">
                <div
                  class="h-2 rounded-full transition-all duration-500"
                  :class="section.color"
                  :style="{ width: section.percentage + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 홀짝 패턴 -->
        <div
          class="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg"
        >
          <h4 class="text-lg font-semibold mb-4 flex items-center">
            <div class="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
            홀수/짝수 패턴
          </h4>
          <div class="grid grid-cols-3 gap-4">
            <div
              class="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg"
            >
              <div class="text-2xl font-bold text-red-600">
                {{ oddEvenPattern.oddHeavy }}%
              </div>
              <div class="text-xs text-red-500 mt-1">
                홀수 많음<br />(4-5개)
              </div>
            </div>
            <div
              class="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg"
            >
              <div class="text-2xl font-bold text-blue-600">
                {{ oddEvenPattern.balanced }}%
              </div>
              <div class="text-xs text-blue-500 mt-1">균형<br />(3개씩)</div>
            </div>
            <div
              class="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg"
            >
              <div class="text-2xl font-bold text-green-600">
                {{ oddEvenPattern.evenHeavy }}%
              </div>
              <div class="text-xs text-green-500 mt-1">
                짝수 많음<br />(4-5개)
              </div>
            </div>
          </div>
        </div>

        <!-- 합계 범위 분석 -->
        <div
          class="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg"
        >
          <h4 class="text-lg font-semibold mb-4 flex items-center">
            <div class="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
            번호 합계 분석
          </h4>
          <div class="space-y-4">
            <div class="text-center">
              <div class="text-3xl font-bold text-orange-600 mb-2">
                {{ averageSum }}
              </div>
              <div class="text-sm text-gray-600">평균 합계</div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div class="text-center p-3 bg-white/60 rounded-lg">
                <div class="text-lg font-bold text-gray-700">
                  {{ sumRange.min }}
                </div>
                <div class="text-xs text-gray-500">최소값</div>
              </div>
              <div class="text-center p-3 bg-white/60 rounded-lg">
                <div class="text-lg font-bold text-gray-700">
                  {{ sumRange.max }}
                </div>
                <div class="text-xs text-gray-500">최대값</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 광고 영역 2 -->
    <AdSenseUnit ad-slot="6444005568" />

    <!-- 최근 트렌드 -->
    <div class="glass-card p-6">
      <h3 class="text-lg font-semibold mb-6 text-gray-800">최근 20회차 트렌드</h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="text-center space-y-4">
          <h4 class="text-sm font-medium text-gray-500">핫 넘버</h4>
          <div class="flex flex-wrap justify-center gap-2">
            <span
              v-for="number in hotNumbers"
              :key="number"
              class="flex items-center justify-center w-12 h-12 rounded-full font-bold text-white text-base shadow-lg transform hover:scale-110 transition-transform"
              :class="getBallClass(number)"
            >
              {{ number }}
            </span>
          </div>
        </div>
        <div class="text-center space-y-4">
          <h4 class="text-sm font-medium text-gray-500">콜드 넘버</h4>
          <div class="flex flex-wrap justify-center gap-2">
            <span
              v-for="number in coldNumbers"
              :key="number"
              class="flex items-center justify-center w-12 h-12 rounded-full font-bold text-white text-base shadow-lg transform hover:scale-110 transition-transform opacity-50"
              :class="getBallClass(number)"
            >
              {{ number }}
            </span>
          </div>
        </div>
        <div class="text-center space-y-4">
          <h4 class="text-sm font-medium text-gray-500">균형 번호</h4>
          <div class="flex flex-wrap justify-center gap-2">
            <span
              v-for="number in balancedNumbers"
              :key="number"
              class="flex items-center justify-center w-12 h-12 rounded-full font-bold text-white text-base shadow-lg transform hover:scale-110 transition-transform ring-2 ring-yellow-300"
              :class="getBallClass(number)"
            >
              {{ number }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 광고 영역 3 -->
    <AdSenseUnit ad-slot="9014328588" />

    <!-- 차트 섹션 -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- 번호별 출현 빈도 차트 -->
      <div class="glass-card p-6">
        <h3 class="text-xl font-semibold mb-4">번호별 출현 빈도</h3>
        <div class="relative h-80">
          <canvas ref="frequencyChart"></canvas>
        </div>
      </div>

      <!-- 최근 출현 패턴 차트 -->
      <div class="glass-card p-6">
        <h3 class="text-xl font-semibold mb-4">최근 출현 패턴 (최근 20회)</h3>
        <div class="relative h-80">
          <canvas ref="patternChart"></canvas>
        </div>
      </div>
    </div>

    <!-- 홀수/짝수 분포 차트 -->
    <div class="glass-card p-6">
      <h3 class="text-xl font-semibold mb-4">홀수/짝수 및 고저 번호 분포</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="relative h-64">
          <canvas ref="oddEvenChart"></canvas>
        </div>
        <div class="relative h-64">
          <canvas ref="highLowChart"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import { Chart, registerables } from "chart.js";
import { loadLottoData, calculateStats } from "../utils/lottoUtils";
import AdSenseUnit from "./AdSenseUnit.vue";

Chart.register(...registerables);

export default {
  name: "Dashboard",
  setup() {
    const lottoData = ref([]);
    const loading = ref(true);
    const error = ref(null);

    // 차트 참조
    const frequencyChart = ref(null);
    const patternChart = ref(null);
    const oddEvenChart = ref(null);
    const highLowChart = ref(null);

    // 차트 인스턴스
    let frequencyChartInstance = null;
    let patternChartInstance = null;
    let oddEvenChartInstance = null;
    let highLowChartInstance = null;

    // 패턴 분석 데이터
    const consecutivePatterns = ref({
      two: 0,
      three: 0,
      four: 0,
    });

    const sectionDistribution = ref([]);
    const oddEvenPattern = ref({
      oddHeavy: 0,
      balanced: 0,
      evenHeavy: 0,
    });

    const sumRange = ref({ min: 0, max: 0 });
    const hotNumbers = ref([]);
    const coldNumbers = ref([]);
    const balancedNumbers = ref([]);

    // 데이터 로드
    const loadData = async () => {
      try {
        loading.value = true;
        error.value = null;
        lottoData.value = await loadLottoData();
        console.log(`로또 데이터 로드 완료: ${lottoData.value.length}개 회차`);

        // 패턴 분석 수행
        analyzePatterns();
      } catch (err) {
        error.value = "데이터를 불러오는데 실패했습니다.";
        console.error("데이터 로드 실패:", err);
      } finally {
        loading.value = false;
      }
    };

    // 패턴 분석 함수
    const analyzePatterns = () => {
      if (lottoData.value.length === 0) return;

      // 연속번호 패턴 분석
      analyzeConsecutivePatterns();

      // 구간별 분포 분석
      analyzeSectionDistribution();

      // 홀짝 패턴 분석
      analyzeOddEvenPattern();

      // 합계 범위 분석
      analyzeSumRange();

      // 최근 트렌드 분석
      analyzeRecentTrends();
    };

    // 연속번호 패턴 분석
    const analyzeConsecutivePatterns = () => {
      let twoConsecutive = 0;
      let threeConsecutive = 0;
      let fourOrMoreConsecutive = 0;

      lottoData.value.forEach((draw) => {
        const numbers = draw.numbers.sort((a, b) => a - b);
        let consecutive = 1;
        let maxConsecutive = 1;

        for (let i = 1; i < numbers.length; i++) {
          if (numbers[i] === numbers[i - 1] + 1) {
            consecutive++;
          } else {
            maxConsecutive = Math.max(maxConsecutive, consecutive);
            consecutive = 1;
          }
        }
        maxConsecutive = Math.max(maxConsecutive, consecutive);

        if (maxConsecutive >= 2) twoConsecutive++;
        if (maxConsecutive >= 3) threeConsecutive++;
        if (maxConsecutive >= 4) fourOrMoreConsecutive++;
      });

      const total = lottoData.value.length;
      consecutivePatterns.value = {
        two: Math.round((twoConsecutive / total) * 100),
        three: Math.round((threeConsecutive / total) * 100),
        four: Math.round((fourOrMoreConsecutive / total) * 100),
      };
    };

    // 구간별 분포 분석
    const analyzeSectionDistribution = () => {
      const sections = [
        { range: "1-10", min: 1, max: 10, count: 0, color: "bg-red-500" },
        { range: "11-20", min: 11, max: 20, count: 0, color: "bg-orange-500" },
        { range: "21-30", min: 21, max: 30, count: 0, color: "bg-yellow-500" },
        { range: "31-40", min: 31, max: 40, count: 0, color: "bg-green-500" },
        { range: "41-45", min: 41, max: 45, count: 0, color: "bg-blue-500" },
      ];

      let totalNumbers = 0;

      lottoData.value.forEach((draw) => {
        draw.numbers.forEach((number) => {
          totalNumbers++;
          sections.forEach((section) => {
            if (number >= section.min && number <= section.max) {
              section.count++;
            }
          });
        });
      });

      sectionDistribution.value = sections.map((section) => ({
        ...section,
        percentage: Math.round((section.count / totalNumbers) * 100),
      }));
    };

    // 홀짝 패턴 분석
    const analyzeOddEvenPattern = () => {
      let oddHeavy = 0; // 홀수 4-5개
      let balanced = 0; // 홀수 3개
      let evenHeavy = 0; // 홀수 1-2개

      lottoData.value.forEach((draw) => {
        const oddCount = draw.numbers.filter((n) => n % 2 === 1).length;

        if (oddCount >= 4) oddHeavy++;
        else if (oddCount === 3) balanced++;
        else evenHeavy++;
      });

      const total = lottoData.value.length;
      oddEvenPattern.value = {
        oddHeavy: Math.round((oddHeavy / total) * 100),
        balanced: Math.round((balanced / total) * 100),
        evenHeavy: Math.round((evenHeavy / total) * 100),
      };
    };

    // 합계 범위 분석
    const analyzeSumRange = () => {
      const sums = lottoData.value.map((draw) =>
        draw.numbers.reduce((sum, num) => sum + num, 0)
      );

      sumRange.value = {
        min: Math.min(...sums),
        max: Math.max(...sums),
      };
    };

    // 최근 트렌드 분석 (최근 20회차)
    const analyzeRecentTrends = () => {
      const recentData = lottoData.value.slice(-20);
      const frequency = {};

      // 번호별 출현 횟수 계산
      for (let i = 1; i <= 45; i++) {
        frequency[i] = 0;
      }

      recentData.forEach((draw) => {
        draw.numbers.forEach((number) => {
          frequency[number]++;
        });
      });

      // 핫넘버 (3회 이상 출현)
      hotNumbers.value = Object.keys(frequency)
        .map(Number)
        .filter((num) => frequency[num] >= 3)
        .sort((a, b) => frequency[b] - frequency[a])
        .slice(0, 6);

      // 콜드넘버 (0-1회 출현)
      coldNumbers.value = Object.keys(frequency)
        .map(Number)
        .filter((num) => frequency[num] <= 1)
        .sort((a, b) => frequency[a] - frequency[b])
        .slice(0, 6);

      // 균형번호 (2회 출현)
      balancedNumbers.value = Object.keys(frequency)
        .map(Number)
        .filter((num) => frequency[num] === 2)
        .sort(() => Math.random() - 0.5)
        .slice(0, 6);
    };

    // 계산된 속성들

    const lastUpdate = computed(() => {
      if (lottoData.value.length === 0) return 0;
      return Math.max(...lottoData.value.map((item) => item.round));
    });

    const latestNumbers = computed(() => {
      if (lottoData.value.length === 0) return null;
      const latest = lottoData.value.find(
        (item) => item.round === lastUpdate.value
      );
      return latest || lottoData.value[lottoData.value.length - 1];
    });

    const stats = computed(() => {
      if (lottoData.value.length === 0) return null;
      return calculateStats(lottoData.value);
    });

    const mostFrequentNumber = computed(() => {
      if (!stats.value) return { number: 0, count: 0 };

      const frequency = stats.value.frequency;
      let maxCount = 0;
      let maxNumber = 1;

      for (let i = 1; i <= 45; i++) {
        if (frequency[i] > maxCount) {
          maxCount = frequency[i];
          maxNumber = i;
        }
      }

      return { number: maxNumber, count: maxCount };
    });

    const oddPercentage = computed(() => {
      return stats.value ? Math.round(stats.value.oddPercentage) : 0;
    });

    const consecutivePercentage = computed(() => {
      return stats.value ? Math.round(stats.value.consecutivePercentage) : 0;
    });

    const averageSum = computed(() => {
      return stats.value ? Math.round(stats.value.averageSum) : 0;
    });

    const getBallClass = (number) => {
      if (number <= 10) return "bg-gradient-to-r from-yellow-400 to-amber-500"; // 1~10: 황금색
      if (number <= 20) return "bg-gradient-to-r from-sky-400 to-blue-500"; // 11~20: 하늘색/파란색
      if (number <= 30) return "bg-gradient-to-r from-pink-400 to-rose-500"; // 21~30: 핑크/장미색
      if (number <= 40) return "bg-gradient-to-r from-gray-500 to-gray-600"; // 31~40: 회색
      return "bg-gradient-to-r from-lime-400 to-green-500"; // 41~45: 연두색
    };

    const formatDate = (date) => {
      return new Intl.DateTimeFormat("ko-KR").format(new Date(date));
    };

    const initializeCharts = () => {
      if (!stats.value || lottoData.value.length === 0) return;

      // 기존 차트 정리
      [
        frequencyChartInstance,
        patternChartInstance,
        oddEvenChartInstance,
        highLowChartInstance,
      ].forEach((chart) => {
        if (chart) {
          chart.destroy();
        }
      });

      // 번호별 출현 빈도 차트
      const frequencyCtx = frequencyChart.value.getContext("2d");
      const frequencyData = stats.value.frequency || {};

      frequencyChartInstance = new Chart(frequencyCtx, {
        type: "bar",
        data: {
          labels: Object.keys(frequencyData)
            .map(Number)
            .sort((a, b) => a - b),
          datasets: [
            {
              label: "출현 횟수",
              data: Object.keys(frequencyData)
                .map(Number)
                .sort((a, b) => a - b)
                .map((num) => frequencyData[num]),
              backgroundColor: "rgba(59, 130, 246, 0.6)",
              borderColor: "rgba(59, 130, 246, 1)",
              borderWidth: 1,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: "rgba(0, 0, 0, 0.1)",
              },
            },
            x: {
              grid: {
                display: false,
              },
            },
          },
        },
      });

      // 최근 패턴 차트
      const patternCtx = patternChart.value.getContext("2d");
      const recentData = lottoData.value.slice(-20);

      patternChartInstance = new Chart(patternCtx, {
        type: "line",
        data: {
          labels: recentData.map((d) => `${d.round}회`),
          datasets:
            recentData[0]?.numbers.map((_, index) => ({
              label: `${index + 1}번째 공`,
              data: recentData.map((d) => d.numbers[index]),
              borderColor: `hsl(${index * 60}, 70%, 50%)`,
              backgroundColor: `hsla(${index * 60}, 70%, 50%, 0.1)`,
              tension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
            })) || [],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
            },
          },
          scales: {
            y: {
              min: 1,
              max: 45,
              grid: {
                color: "rgba(0, 0, 0, 0.1)",
              },
            },
            x: {
              grid: {
                display: false,
              },
            },
          },
        },
      });

      // 홀수/짝수 차트
      const oddEvenCtx = oddEvenChart.value.getContext("2d");
      const oddCount = stats.value.oddCount || 0;
      const evenCount = stats.value.evenCount || 0;

      oddEvenChartInstance = new Chart(oddEvenCtx, {
        type: "doughnut",
        data: {
          labels: ["홀수", "짝수"],
          datasets: [
            {
              data: [oddCount, evenCount],
              backgroundColor: ["#10b981", "#3b82f6"],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
            },
          },
        },
      });

      // 고저 번호 차트
      const highLowCtx = highLowChart.value.getContext("2d");
      const lowCount = stats.value.lowCount || 0;
      const highCount = stats.value.highCount || 0;

      highLowChartInstance = new Chart(highLowCtx, {
        type: "doughnut",
        data: {
          labels: ["저번호 (1-22)", "고번호 (23-45)"],
          datasets: [
            {
              data: [lowCount, highCount],
              backgroundColor: ["#f59e0b", "#ef4444"],
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
            },
          },
        },
      });
    };

    onMounted(async () => {
      await loadData();
      // DOM이 렌더링된 후 차트 초기화
      setTimeout(() => {
        if (frequencyChart.value && stats.value) {
          initializeCharts();
        }
      }, 100);
    });

    onBeforeUnmount(() => {
      // 컴포넌트 언마운트 시 차트 인스턴스 정리
      [
        frequencyChartInstance,
        patternChartInstance,
        oddEvenChartInstance,
        highLowChartInstance,
      ].forEach((chart) => {
        if (chart) {
          chart.destroy();
        }
      });
    });

    return {
      lottoData,
      loading,
      error,
      lastUpdate,
      latestNumbers,
      mostFrequentNumber,
      oddPercentage,
      consecutivePercentage,
      averageSum,
      consecutivePatterns,
      sectionDistribution,
      oddEvenPattern,
      sumRange,
      hotNumbers,
      coldNumbers,
      balancedNumbers,
      getBallClass,
      formatDate,
      frequencyChart,
      patternChart,
      oddEvenChart,
      highLowChart,
    };
  },
};
</script>
